// src/features/engine-play/stores/engine.store.ts
import { create } from 'zustand'
import { getCookie } from '@/lib/cookies'
import type { GalgameSegments, UIMessage } from '../types/chat-model'
import type { EngineInfo } from '../types/engine'
import { MAX_SHARE_MESSAGES } from '../types/share'

// Cookie key for background image setting
const BACKGROUND_IMG_COOKIE_KEY = 'isOpenBackgroundImg'

/**
 * EngineStore - 纯 UI 状态管理
 *
 * 职责：
 * 1. 管理聊天模式切换 (chat/galgame)
 * 2. 管理 Galgame 播放状态
 * 3. 管理消息列表
 * 4. 管理当前引擎信息
 *
 * 注意：所有业务意图处理（API 调用等）已移至 useEnginePlayActions
 */

interface EngineState {
  mode: {
    mode: 'chat' | 'galgame'
    setMode: (mode: 'chat' | 'galgame') => void
  }

  galgameState: {
    currentMessageIndex: number
    galgameIndex: number
    galgameSegments: GalgameSegments

    // 纯数据 setter（业务逻辑已移至 useGalgameController）
    setCurrentMessageIndex: (index: number) => void
    setGalgameIndex: (index: number) => void
    setGalgameSegments: (segments: GalgameSegments) => void
    /** 批量更新 galgame 状态 */
    updateGalgameState: (updates: {
      currentMessageIndex?: number
      galgameIndex?: number
      galgameSegments?: GalgameSegments
    }) => void
    /** 重置 galgame 状态 */
    resetGalgameState: () => void
  }

  messages: {
    messages: UIMessage[] | null
    /** 是否有更多历史消息 */
    hasMore: boolean
    /** 是否正在加载更多 */
    isLoadingMore: boolean

    /** 设置消息列表（用于新消息追加） */
    setMessages: (messages: UIMessage[]) => void
    /** 向列表开头插入历史消息 */
    prependMessages: (messages: UIMessage[]) => void
    /** 从分页数据初始化消息列表 */
    initFromPagination: (messages: UIMessage[], hasMore: boolean) => void
    /** 设置加载更多状态 */
    setLoadingMore: (loading: boolean) => void
    /** 设置是否有更多历史 */
    setHasMore: (hasMore: boolean) => void
    /** 清空消息列表 */
    clearMessages: () => void
  }

  engine: {
    currentEngine: EngineInfo | null
    setCurrentEngine: (engine: EngineInfo | null) => void
    clearEngine: () => void

    isOpenBackgroundImg: boolean
    setIsOpenBackgroundImg: (isOpenBackgroundImg: boolean) => void

    isUseCoverAsBackground: boolean
    setIsUseCoverAsBackground: (useCover: boolean) => void
    originalBackgroundUrl: string | null
  }

  chatSettings: {
    isOpen: boolean
    setIsOpen: (isOpen: boolean) => void
    open: () => void
    close: () => void
  }

  /**
   * choice 切片 - 管理轮询状态
   * 用于持久化 taskId、isPolling 状态，防止组件卸载时丢失
   */
  choice: {
    /** 当前轮询的任务 ID */
    taskId: string | undefined
    /** 期望的任务 ID，用于验证返回数据的归属 */
    expectedTaskId: string | undefined
    /** 是否正在轮询中 */
    isPolling: boolean
    /** 当前轮询关联的 sessionId */
    sessionId: string | undefined
    /** 轮询完成后等待处理的结果 */
    pendingResult: unknown | undefined

    /** 开始轮询任务 */
    startPolling: (taskId: string, sessionId: string) => void
    /** 停止轮询并清除状态 */
    stopPolling: () => void
    /** 设置待处理的结果 */
    setPendingResult: (result: unknown) => void
    /** 清除待处理的结果 */
    clearPendingResult: () => void
    /** 完全重置轮询状态（用于切换会话时） */
    resetPolling: () => void
  }

  /**
   * shareMode 切片 - 管理分享模式状态
   * 用于选择消息并生成分享图片
   */
  shareMode: {
    /** 是否处于分享模式 */
    isShareMode: boolean
    /** 已选中的消息 ID 集合 */
    selectedMessageIds: Set<string>
    /** 设置分享模式 */
    setShareMode: (isOpen: boolean) => void
    /** 切换消息选中状态 */
    toggleMessageSelection: (id: string) => void
    /** 清空选择 */
    clearSelection: () => void
  }
}

export const useEngineStore = create<EngineState>()((set) => {
  return {
    mode: {
      mode: 'chat',
      setMode: (mode) =>
        set((state) => ({ ...state, mode: { ...state.mode, mode } })),
    },

    engine: {
      currentEngine: null,
      originalBackgroundUrl: null,
      setCurrentEngine: (engine: EngineInfo | null) =>
        set((state) => ({
          ...state,
          engine: {
            ...state.engine,
            currentEngine: engine,
            originalBackgroundUrl: engine?.backgroundImageUrl ?? null,
            isUseCoverAsBackground: false, // 切换引擎时重置
          },
        })),
      clearEngine: () =>
        set((state) => ({
          ...state,
          engine: {
            ...state.engine,
            currentEngine: null,
            originalBackgroundUrl: null,
            isUseCoverAsBackground: false,
          },
        })),

      isOpenBackgroundImg: getCookie(BACKGROUND_IMG_COOKIE_KEY) !== 'false',
      setIsOpenBackgroundImg: (isOpenBackgroundImg: boolean) =>
        set((state) => ({
          ...state,
          engine: { ...state.engine, isOpenBackgroundImg },
        })),

      isUseCoverAsBackground: false,
      setIsUseCoverAsBackground: (useCover: boolean) =>
        set((state) => {
          const { currentEngine, originalBackgroundUrl } = state.engine
          if (!currentEngine) return state

          const targetUrl = useCover
            ? currentEngine.coverURL || currentEngine.backgroundImageUrl
            : originalBackgroundUrl || currentEngine.backgroundImageUrl

          return {
            ...state,
            engine: {
              ...state.engine,
              isUseCoverAsBackground: useCover,
              currentEngine: {
                ...currentEngine,
                backgroundImageUrl: targetUrl,
              },
            },
          }
        }),
    },

    galgameState: {
      currentMessageIndex: 0,
      galgameIndex: 0,
      galgameSegments: [],

      setCurrentMessageIndex: (index: number) =>
        set((state) => ({
          ...state,
          galgameState: { ...state.galgameState, currentMessageIndex: index },
        })),

      setGalgameIndex: (index: number) =>
        set((state) => ({
          ...state,
          galgameState: { ...state.galgameState, galgameIndex: index },
        })),

      setGalgameSegments: (segments: GalgameSegments) =>
        set((state) => ({
          ...state,
          galgameState: { ...state.galgameState, galgameSegments: segments },
        })),

      updateGalgameState: (updates) =>
        set((state) => ({
          ...state,
          galgameState: {
            ...state.galgameState,
            ...(updates.currentMessageIndex !== undefined && {
              currentMessageIndex: updates.currentMessageIndex,
            }),
            ...(updates.galgameIndex !== undefined && {
              galgameIndex: updates.galgameIndex,
            }),
            ...(updates.galgameSegments !== undefined && {
              galgameSegments: updates.galgameSegments,
            }),
          },
        })),

      resetGalgameState: () =>
        set((state) => ({
          ...state,
          galgameState: {
            ...state.galgameState,
            currentMessageIndex: 0,
            galgameIndex: 0,
            galgameSegments: [],
          },
        })),
    },

    messages: {
      messages: [],
      hasMore: false,
      isLoadingMore: false,

      setMessages: (newMessages: UIMessage[]) => {
        set((state) => ({
          ...state,
          messages: { ...state.messages, messages: newMessages },
        }))
      },

      prependMessages: (newMessages: UIMessage[]) => {
        set((state) => {
          const currentMessages = state.messages.messages || []
          return {
            ...state,
            messages: {
              ...state.messages,
              messages: [...newMessages, ...currentMessages],
            },
          }
        })
      },

      initFromPagination: (newMessages: UIMessage[], hasMore: boolean) => {
        set((state) => ({
          ...state,
          messages: {
            ...state.messages,
            messages: newMessages,
            hasMore,
            isLoadingMore: false,
          },
        }))
      },

      setLoadingMore: (loading: boolean) => {
        set((state) => ({
          ...state,
          messages: { ...state.messages, isLoadingMore: loading },
        }))
      },

      setHasMore: (hasMore: boolean) => {
        set((state) => ({
          ...state,
          messages: { ...state.messages, hasMore },
        }))
      },

      clearMessages: () => {
        set((state) => ({
          ...state,
          messages: {
            ...state.messages,
            messages: [],
            hasMore: false,
            isLoadingMore: false,
          },
        }))
      },
    },

    chatSettings: {
      isOpen: false,
      setIsOpen: (isOpen: boolean) =>
        set((state) => ({
          ...state,
          chatSettings: { ...state.chatSettings, isOpen },
        })),
      open: () =>
        set((state) => ({
          ...state,
          chatSettings: { ...state.chatSettings, isOpen: true },
        })),
      close: () =>
        set((state) => ({
          ...state,
          chatSettings: { ...state.chatSettings, isOpen: false },
        })),
    },

    choice: {
      taskId: undefined,
      expectedTaskId: undefined,
      isPolling: false,
      sessionId: undefined,
      pendingResult: undefined,

      startPolling: (taskId: string, sessionId: string) =>
        set((state) => ({
          ...state,
          choice: {
            ...state.choice,
            taskId,
            expectedTaskId: taskId,
            isPolling: true,
            sessionId,
            pendingResult: undefined,
          },
        })),

      stopPolling: () =>
        set((state) => ({
          ...state,
          choice: {
            ...state.choice,
            taskId: undefined,
            expectedTaskId: undefined,
            isPolling: false,
          },
        })),

      setPendingResult: (result: unknown) =>
        set((state) => ({
          ...state,
          choice: { ...state.choice, pendingResult: result },
        })),

      clearPendingResult: () =>
        set((state) => ({
          ...state,
          choice: {
            ...state.choice,
            pendingResult: undefined,
            sessionId: undefined,
          },
        })),

      resetPolling: () =>
        set((state) => ({
          ...state,
          choice: {
            ...state.choice,
            taskId: undefined,
            expectedTaskId: undefined,
            isPolling: false,
            sessionId: undefined,
            pendingResult: undefined,
          },
        })),
    },
    shareMode: {
      isShareMode: false,
      selectedMessageIds: new Set<string>(),

      setShareMode: (isOpen: boolean) =>
        set((state) => ({
          ...state,
          shareMode: {
            ...state.shareMode,
            isShareMode: isOpen,
            // 退出分享模式时清空选择
            selectedMessageIds: isOpen
              ? state.shareMode.selectedMessageIds
              : new Set(),
          },
        })),

      toggleMessageSelection: (id: string) =>
        set((state) => {
          const newSet = new Set(state.shareMode.selectedMessageIds)
          if (newSet.has(id)) {
            newSet.delete(id)
          } else if (newSet.size < MAX_SHARE_MESSAGES) {
            // 最多选10条
            newSet.add(id)
          }
          return {
            ...state,
            shareMode: { ...state.shareMode, selectedMessageIds: newSet },
          }
        }),

      clearSelection: () =>
        set((state) => ({
          ...state,
          shareMode: { ...state.shareMode, selectedMessageIds: new Set() },
        })),
    },
  }
})
